<template>
  <config-base class="workspace-reservation" @view-details="showPlanDetailDialog" :config-plan-tile-status="configPlanTileStatus">
    <div slot="config">
      <zm-form label-position="top" class="opc-config-form">
        <zm-form-item :label="$t('billing.config.select_number_spaces')">
          <license-tip
            :type="planType"
            :number="licenseNumber"
            :additionalSpans="additionalLicenseTipSpans"
            @click="handleLicenseTipsClick"
          />
          <input-number-with-popover
            size="large"
            controls-position="both"
            class="gap8"
            v-model="licenseNumber"
            :min="minLicenses"
            :max="maxLicenses"
            :decrease-tips="decreaseTips"
            :label="`${title} select the number of spaces`"
            :dataTracking="{ buyInteraction: 'number of spaces', planName: planTrackingName }"
            @change="updatePlanLicense"
          />
          <alert v-if="mx_downSellLicenseErrorMessage" type="info" v-html="mx_downSellLicenseErrorMessage" class="gap16 workspace-reservation__license-alert"/>
          <bought-license :type="planType"/>
        </zm-form-item>
        <zm-form-item :label="$t('billing.select_billing_cycle')">
          <billed-billing-cycle-radio
            v-model="billingCycle"
            class="gap8"
            :billingCycleList="billingCycleList"
            :unit="currentPlanBasicInfo.unit"
            @change="updateBillingCycle"
          />
        </zm-form-item>
        <preview-payment class="gap24" />
      </zm-form>
    </div>
    <template #footer v-if="isShowRemoveBtn">
      <zm-button
        ghost
        data-testid="checkout-cart-workspace-reservation-button-remove"
        type="link"
        @click="removePlanByKey"
        :aria-label="`remove ${title} plan`"
      >
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script>
import { isEmpty, pick } from 'lodash';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { plansof } from '@/utils/chain';
import { getCommonContactSalesLink } from '@/utils/i18n-shortcuts';
import ConfigBase from '@/components/config-base.vue';
import BilledBillingCycleRadio from '@/components/service/config-billing-cycle/billed-cycle/index.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { planUnitMap } from '@/common/product-unit';
import { planTypes, trackingPlanTypes } from '@/common/constants.js';
import LicenseTip from '../ZOOM_MEETING/license-tip.vue';
import plansMixin from '../mx/plan.js';
import addonMixin from '../mx/addon.js';
import planDetailsDialogMixin from './components/plan-details/plan-details-dialog.js';
import workspaceReservationMixin from './mixin.js';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import autofill from "@/plans/mx/autofill";

const ConfigBaseComp = {
  name: 'WorkspaceReservationBase',
  extends: ConfigBase,
  mixins: [workspaceReservationMixin, autofill],
  key: planTypes[planTypes.WORKSPACE_RESERVATION],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: true,
      unit: planUnitMap.space,
    };
  },
  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `${this.trackingPlanKey} - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
  },
};

export default {
  name: 'WorkspaceReservationConfig',
  mixins: [workspaceReservationMixin, plansMixin, addonMixin, planDetailsDialogMixin],
  components: {
    ConfigBase: ConfigBaseComp,
    InputNumberWithPopover,
    Alert,
    PreviewPayment,
    LicenseTip,
    BilledBillingCycleRadio,
    BoughtLicense
  },
  data() {
    return {
      billingCycle: '',
      licenseNumber: 1,
      unit: planUnitMap.space,
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'basicConfig',
      'currentEditPlan',
    ]),
    showLicenseTip() {
      return this.licenseNumber >= this.currentPlanBasicInfo.max;
    },
    additionalLicenseTipSpans() {
      return {
        [this.planType]: [
          {
            value: this.currentPlanBasicInfo.min,
            default: true,
            text: '',
          },
          {
            value: this.currentPlanBasicInfo.max,
            text: this.$t('billing.visitor_management.more_users_tip', {
              quantity: this.currentPlanBasicInfo.max,
              url: getCommonContactSalesLink(),
            }),
          },
        ],
      };
    },
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },

    currentPlanBasicInfo() {
      return pick(this.planList[0], ['min', 'max', 'unit', 'category']);
    },

    disableDownSell() {
      // Check account MRR > 500
      return this.basicConfig?.downSellData?.disableDownSell;
    },

    currentEditPlanLicense() {
      return this.currentEditPlan?.cartInfo?.selected_number ?? 0;
    },

    previousQuantity() {
      return this.currentEditPlanLicense > 0 ? this.currentEditPlanLicense : this.currentPlanBasicInfo.min;
    },

    minLicenses() {
      return this.disableDownSell || !this.isStandardPrice ? this.previousQuantity : this.currentPlanBasicInfo.min;
    },

    maxLicenses() {
      return Math.max(this.currentPlanBasicInfo.max, this.currentEditPlanLicense);
    },

    decreaseTips() {
      if (this.licenseNumber === this.previousQuantity) {
        if (this.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }
        if (this.forNonStandardPriceTips) {
          return this.forNonStandardPriceTips;
        }
      }

      return '';
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicense() {
      return (billingCycle) => {
        return (
          this.isEditFlow &&
          this.basicConfig?.isBulkLicenseAccount &&
          this.currentEditPlan?.billing_cycle !== billingCycle
        );
      };
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },

    planTrackingName() {
      return trackingPlanTypes.WORKSPACE_RESERVATION;
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),

    /**
     * update license number
     * @param {number} newLicense
     */
    updatePlanLicense(newLicense) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newLicense },
      });
      this.mx_validDownSellLicense(newLicense);
    },

    /**
     * update billing cycle
     * @param {object} oldPlan
     * @param {object} newPlan
     */
    async updateBillingCycle(oldPlan, newPlan) {
      this.$tracking.buyInteraction(
        'billing cycle',
        `${this.planTrackingName} - toggled billed ${newPlan.billing_cycle}`,
      );

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        { ...newPlan, cartInfo: { selected_number: this.licenseNumber } },
      ]);
    },

    handleLicenseTipsClick(e) {
      if (e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.planTrackingName} - the maximum amount is ${this.currentPlanBasicInfo.max} licenses - contact sales`,
        );
      }
    },
  },
  created() {
    if (!isEmpty(this.selectedPlan)) {
      this.billingCycle = this.selectedPlan.billing_cycle;
      this.licenseNumber = this.selectedPlan.cartInfo?.selected_number;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.licenseNumber);
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/zoom-card.scss';

.gap8 {
  margin-top: 8px;
}
.gap16 {
  margin-top: 16px;
}
.gap24 {
  margin-top: 24px;
}

.workspace-reservation {
  ::v-deep .zm-form-item {
    margin-bottom: 16px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  &__license-alert {
    padding: 16px;
    color: #666487;
    font-size: 14px;
    line-height: 1.5;
    display: block;
    a {
      font-weight: 450;
    }
  }
}
</style>
